import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Apartment, DoneAll, Error as ErrorIcon } from '@mui/icons-material';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  styled,
  Tooltip,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';

import { ALL_FACILITIES } from '@/hooks/useFacilitiesQuery';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { UserModel } from '@/models/UserModel';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import { UserFacilityAccess } from '@/types/users';

import CrmScreenerSelectAllButton from './CrmScreenerSelectAllButton';
import FacilitySearch from './FacilitySearch';
import { screenerAllFacilitiesSelectedLastValueAtom } from './screenerAllFacilitiesSelectedLastValueAtom';

export const FacilitySelectorListItemButton = styled(ListItemButton)({
  p: '12px 16px',
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#0A1E28',
    lineHeight: '20px'
  },
  '& .MuiListItemText-secondary': {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 400,
    color: '#667A86',
    lineHeight: '16px'
  },
  '& .MuiSvgIcon-root': {
    color: '#9AAEBB'
  },
  '& .MuiListItemIcon-root': {
    minWidth: 32
  }
});

interface FacilitySelectorPopoverProps {
  selectedFacilityId: string | null;
  anchorEl: HTMLDivElement;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: UserModel;
  onSelectFacility: (facilityId: string) => void;
  canSelectAllFacilities: boolean;
  isCRM: boolean;
  billingDisabledFacilityIds: Set<string>;
  anyFacilityBillingDisabled: boolean;
}

const FacilitySelectorPopover: React.FC<FacilitySelectorPopoverProps> = ({
  selectedFacilityId,
  onSelectFacility,
  isOpen,
  user,
  setIsOpen,
  anchorEl,
  canSelectAllFacilities,
  isCRM,
  billingDisabledFacilityIds,
  anyFacilityBillingDisabled
}) => {
  const [, setIsAllFacilitiesSelectedLastValue] = useAtom(
    screenerAllFacilitiesSelectedLastValueAtom
  );
  const navigate = useNavigate();

  const isMobileViewport = useIsMobileViewport();
  const [inputValue, setInputValue] = React.useState('');
  const [filteredFacilities, setFilteredFacilities] = React.useState<
    UserFacilityAccess[]
  >(user.user_facility_accesses!);
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { handleSearchKeyDown, handleListKeyDown } = useKeyboardNavigation({
    searchInputRef,
    listRef
  });

  useEffect(() => {
    const filteredFacilities =
      user.user_facility_accesses?.filter((facility) => {
        return facility.facility?.name
          ?.toLowerCase()
          .includes((inputValue ?? '').toLowerCase());
      }) ?? [];
    setFilteredFacilities(
      filteredFacilities.sort((a, b) => {
        return a.facility.name.localeCompare(b.facility.name);
      })
    );
  }, [inputValue, user.user_facility_accesses]);

  const navigatingAwayRef = useRef(false);

  const handleFacilityChange = (facilityId: string) => {
    if (isAllFacilitiesSelected) {
      navigatingAwayRef.current = true;
      // Clean all_facilities query string param if a different facility is selected
      setIsAllFacilitiesSelectedLastValue(false);
      navigate(location.pathname);
    }
    onSelectFacility(facilityId);
  };

  // Handle optional query string param to set the facility ID on redirection
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const facilityIdFromQuery = searchParams.get('set_facility_id');

    if (facilityIdFromQuery && facilityIdFromQuery !== selectedFacilityId) {
      onSelectFacility(facilityIdFromQuery);

      searchParams.delete('set_facility_id');
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true
      });
    }
  }, []);

  return (
    <Popover
      PaperProps={{
        sx: {
          maxHeight: 400,
          width: isMobileViewport ? '100%' : 265,
          boxShadow: '0px 5px 20px 2px rgba(44, 58, 66, 0.15)',
          borderRadius: '8px'
        }
      }}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
    >
      {user.user_facility_accesses?.length &&
        user.user_facility_accesses?.length > 10 && (
          <FacilitySearch
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            autoFocus={true}
            onKeyDown={handleSearchKeyDown}
            inputRef={searchInputRef}
          />
        )}
      <List
        dense
        sx={{ p: 0 }}
        role="listbox"
        ref={listRef}
        onKeyDown={handleListKeyDown}
      >
        {!isMobileViewport && (
          <CrmScreenerSelectAllButton
            setIsOpen={setIsOpen}
            hide={inputValue.trim().length > 0}
            anyFacilityBillingDisabled={anyFacilityBillingDisabled}
          />
        )}
        {!isCRM && canSelectAllFacilities && !isMobileViewport && (
          <FacilitySelectorListItemButton
            role="option"
            selected={selectedFacilityId === ALL_FACILITIES}
            disabled={anyFacilityBillingDisabled}
            onClick={() => {
              handleFacilityChange(ALL_FACILITIES);
              setIsOpen(false);
            }}
            sx={anyFacilityBillingDisabled ? { opacity: 0.5 } : undefined}
          >
            <ListItemIcon>
              <DoneAll />
            </ListItemIcon>
            <ListItemText>Select All</ListItemText>
            {anyFacilityBillingDisabled && (
              <Tooltip
                title="All Facilities view is disabled due to an unpaid balance on one or more facilities."
                placement="top"
              >
                <ErrorIcon
                  sx={{
                    fontSize: 18,
                    color: '#D32F2F !important',
                    ml: 1,
                    pointerEvents: 'auto'
                  }}
                />
              </Tooltip>
            )}
          </FacilitySelectorListItemButton>
        )}
        {filteredFacilities.length > 0 ? (
          filteredFacilities.map((item) => {
            const isBillingDisabled = billingDisabledFacilityIds.has(
              item.facility_id
            );
            return (
              <FacilitySelectorListItemButton
                key={item.id}
                role="option"
                id={`community-${item.facility_id}`}
                onClick={() => handleFacilityChange(item.facility_id)}
                selected={
                  isAllFacilitiesSelected
                    ? false
                    : item.facility_id === selectedFacilityId
                }
              >
                <ListItemText secondary={<>{item.facility.address}</>}>
                  {item.facility.name}
                </ListItemText>
                {isBillingDisabled && (
                  <Tooltip
                    title="This facility has been temporarily disabled due to an outstanding balance on your account."
                    placement="top"
                  >
                    <ErrorIcon
                      sx={{ fontSize: 18, color: '#D32F2F !important', ml: 1 }}
                    />
                  </Tooltip>
                )}
              </FacilitySelectorListItemButton>
            );
          })
        ) : (
          <Stack height={200} justifyContent="center" alignItems="center">
            <ListItemText sx={{ textAlign: 'center' }}>
              <Stack direction="column" alignItems="center" gap={2}>
                <Box
                  bgcolor="#B5C9D6"
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={1}
                  height={50}
                  width={50}
                >
                  <Apartment sx={{ color: '#FFFFFF !important' }} />
                </Box>
                <Typography fontSize="16px" fontWeight={500} color="#667A86">
                  No Facilities Found
                </Typography>
              </Stack>
            </ListItemText>
          </Stack>
        )}
      </List>
    </Popover>
  );
};

export default FacilitySelectorPopover;
